import { useState } from "react";
import { Banknote, CreditCard } from "lucide-react";
import { formatCurrency } from "@/lib/money";
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from "@/lib/paymentMethod";
import { recordGuestTabletPayment } from "@/lib/guestTabletApi";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import type {
  GuestTabletInvoice,
  GuestTabletPendingPayment,
  GuestTabletSessionDisplay,
} from "@/types/guestTablet";

type Props = {
  invoice: GuestTabletInvoice;
  pendingPayment: GuestTabletPendingPayment | null;
  workstationCode: string;
  onSessionUpdate: (session: GuestTabletSessionDisplay) => void;
  onRefresh: () => void;
};

function openAmount(invoice: GuestTabletInvoice): number {
  const raw = invoice.open_amount ?? invoice.total_amount;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function pendingMethodLabel(method: string): string {
  return PAYMENT_METHOD_LABELS[method as PaymentMethod] ?? method;
}

export function TabletInvoiceDisplay({
  invoice,
  pendingPayment,
  workstationCode,
  onSessionUpdate,
  onRefresh,
}: Props) {
  const [paying, setPaying] = useState<"cash" | "card" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = openAmount(invoice);
  const canPay = isInvoiceFinalized(invoice.status) && open > 0 && !pendingPayment;

  async function pay(method: "cash" | "card") {
    if (paying || !canPay) return;
    setPaying(method);
    setError(null);
    try {
      const session = await recordGuestTabletPayment(workstationCode, {
        payment_method: method,
        amount: open.toFixed(2),
      });
      onSessionUpdate(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Zahlung fehlgeschlagen.");
      onRefresh();
    } finally {
      setPaying(null);
    }
  }

  return (
    <section className="guest-tablet-step-panel flex min-h-0 flex-1 flex-col gap-4">
      <div className="guest-tablet-card flex min-h-0 flex-1 flex-col p-6 md:p-8">
        <h2 className="guest-tablet-section-title mb-2">Ihre Rechnung</h2>
        {invoice.invoice_number ? (
          <p className="mb-4 text-sm text-emerald-800/70 dark:text-emerald-100/60">
            Rechnungsnummer {invoice.invoice_number}
          </p>
        ) : null}

        <div className="guest-tablet-invoice-lines min-h-0 flex-1 overflow-y-auto">
          <ul className="divide-y divide-emerald-900/10 dark:divide-white/10">
            {invoice.lines.map((line, index) => (
              <li
                key={`${line.description}-${index}`}
                className="flex items-baseline justify-between gap-4 py-3 text-base"
              >
                <span className="min-w-0 font-medium">{line.description}</span>
                <span className="shrink-0 tabular-nums font-semibold">
                  {formatCurrency(line.line_total, invoice.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 border-t border-emerald-900/10 pt-4 dark:border-white/10">
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-lg font-semibold">
              {open > 0 && (canPay || pendingPayment) ? "Zu zahlen" : "Gesamtbetrag"}
            </span>
            <span className="guest-tablet-invoice-total tabular-nums">
              {formatCurrency(
                canPay && open > 0
                  ? String(open)
                  : pendingPayment
                    ? String(pendingPayment.amount)
                    : invoice.total_amount,
                invoice.currency,
              )}
            </span>
          </div>
        </div>
      </div>

      {pendingPayment ? (
        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-emerald-800/10 px-5 py-4 text-center dark:bg-white/10">
            <p className="text-lg font-semibold text-emerald-950 dark:text-emerald-50">
              {pendingMethodLabel(pendingPayment.payment_method)} gewählt
            </p>
            <p className="mt-2 text-base text-emerald-900/80 dark:text-emerald-50/80">
              Bitte warten – die Rezeption bestätigt Ihre Zahlung.
            </p>
          </div>
        </div>
      ) : canPay ? (
        <div className="flex flex-col gap-3">
          <p className="text-center text-lg font-medium text-emerald-950 dark:text-emerald-50">
            Zahlungsart wählen
          </p>
          {error ? <p className="text-center text-sm text-red-700">{error}</p> : null}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="guest-tablet-touch-btn flex flex-col items-center justify-center gap-2 bg-emerald-800 text-white disabled:opacity-60"
              disabled={!!paying}
              onClick={() => void pay("cash")}
            >
              <Banknote className="h-8 w-8" aria-hidden />
              {paying === "cash" ? "Wird übermittelt…" : "Bar"}
            </button>
            <button
              type="button"
              className="guest-tablet-touch-btn flex flex-col items-center justify-center gap-2 bg-emerald-800 text-white disabled:opacity-60"
              disabled={!!paying}
              onClick={() => void pay("card")}
            >
              <CreditCard className="h-8 w-8" aria-hidden />
              {paying === "card" ? "Wird übermittelt…" : "EC-Karte"}
            </button>
          </div>
          <p className="text-center text-sm text-emerald-800/70 dark:text-emerald-100/60">
            Sie können auch an der Rezeption bezahlen.
          </p>
        </div>
      ) : (
        <div className="rounded-xl bg-emerald-800/10 px-5 py-4 text-center text-lg text-emerald-900 dark:bg-white/10 dark:text-emerald-50">
          {invoice.status === "draft"
            ? "Die Rechnung wird noch ausgestellt – bitte einen Moment Geduld."
            : "Vielen Dank – die Zahlung ist eingegangen."}
        </div>
      )}
    </section>
  );
}
